import {
  appId,
  appName,
  clientId,
  clientSecret,
  baseUrl,
  processor,
  fareCapping
} from './settings';
import {logger} from './helperFunctions';

const doFetch = (query, variables, extraHeaders) =>
  fetch(baseUrl, {
    method: 'POST',
    headers: {
      ...{
        'content-type': 'application/json',
        'x-mv-appid': appId,
        'x-mv-timestamp': new Date().getTime()
      },
      ...extraHeaders
    },
    body: JSON.stringify({
      query,
      variables
    })
  }).then(data => data.json());

const refreshAccessToken = async (scope, token) => {
  if (!token || !token.accessToken || !token.refreshToken) {
    logger('token is invalid');
    return Promise.resolve(token);
  }

  const {exp} = JSON.parse(atob(token.accessToken.split('.')[1]));
  if (exp > new Date().getTime() / 1000) {
    return Promise.resolve(token);
  }

  logger('refreshing token');
  return doFetch(
    `mutation($input: OauthTokenInput!) {
    oauthToken (input: $input) {
      accessToken
      refreshToken
    }
  }`,
    {
      input: {
        grantType: 'refresh_token',
        refreshToken: token.refreshToken,
        scope: `${appName}.${scope}`,
        clientId,
        clientSecret
      }
    }
  )
    .then(ret => {
      const newToken = {...token};
      if (ret?.data?.oauthToken?.accessToken) {
        newToken.accessToken = ret.data.oauthToken.accessToken;
        newToken.refreshToken =
          ret?.data?.oauthToken?.refreshToken || token.refreshToken;
        try {
          logger(
            `new access token expires at ${
              JSON.parse(atob(newToken.accessToken.split('.')[1])).exp
            }`
          );
          return Promise.resolve(newToken);
        } catch (err) {
          logger(err);
          return Promise.resolve(token);
        }
      }
      return Promise.resolve(token);
    })
    .catch(err => logger(err));
};

const loginUser = (scope, credentials) =>
  doFetch(
    `mutation($input: OauthTokenInput!) {
    oauthToken (input: $input) {
      accessToken
      refreshToken
    }
  }`,
    {
      input: {
        grantType: 'password',
        username: credentials?.username,
        password: credentials?.password,
        scope: `${appName}.${scope}`,
        clientId,
        clientSecret
      }
    }
  ).catch(err => {
    logger(err);
    return err;
  });

const fetchContent = async (scope, accessToken) =>
  doFetch(
    `query($scope: String!, $tags: [String]!, $accessToken: String) {
    content(scope: $scope) {
      config {
        agency assetBaseUrl hash name scope logo favicon transitCardFront transitCardBack phoneImage defaultLanguage
        riderApp {
          static assets launchFeatures features layouts style ticketLayoutConfigs
          arrivalsLayoutConfigs tripUtilitiesLayout tripToolsLayout
        }
      }
      locales(tags: $tags)
      catalog {
        products { id properties }
        displayItems { slug textPhrase }
      }
      menu(accessToken: $accessToken) {
        currency startNode
        nodes {
          __typename key displayItem
          ... on MenuNodeProduct { id disabled price }
          ... on MenuNodeOption { metaValue }
          ... on MenuNodeList { displayItemTitle next metaName itemsRef items }
          ... on MenuNodeGroup { collapsible items }
          ... on MenuNodeMap { groupAsKey menus unordered map }
        }
      }
    }
  }`,
    {
      scope,
      accessToken,
      tags: ['serr', 'ra', 'common', 'menu', 'rw', 'transition-service'],
      operationName: 'fetchContent'
    }
  ).then(ret => ret?.data?.content || ret);

const fetchAccount = async token =>
  doFetch(
    `
    query ($accessToken: String!, $appId: String!) {
      account(accessToken: $accessToken) {
        profile { 
          birth email username firstName lastName phone pin securityQuestionId securityAnswer securityQuestion terms details externalUserName externalId externalAccountId userId communicationPreference
          reducedFare { 
            id expireDateTime 
          } 
          address {
            thoroughfare premise locality adminArea postalCode country
          }
        }
        tickets(appId: $appId) {
              onDevice { id uuid productId productSku serial sunsetDateTime validToDateTime validFromDateTime properties signature signatureDateTime }
              retrievable { id uuid productId productSku serial sunsetDateTime validToDateTime validFromDateTime properties signature signatureDateTime }
              unretrievable { id uuid productId productSku serial sunsetDateTime validToDateTime validFromDateTime properties signature signatureDateTime }
            }
        orderHistory (limit: 30, offset: 0) {
          lineItems { orderId productId properties meta dateTime quantity amount currentPrice purchasable hideAmount cardType cardNumber }
        }
        paymentMethods {
          ... on PaymentMethod { __typename id nickname processor }
          ... on CardPaymentMethod { accountNumber cardType expirationMonth expirationYear }
          ... on AmountAvailablePaymentMethod { accountNumber amountAvailable amountAvailableType }
          ... on StoredValuePaymentMethod { __typename id nickname processor }
        }
        transitAccounts {
          id nickname transitAccountType userId
          storedValue {
            balance
            buckets { id nickname amount bucketType }
          }
          transitCards {
            uuid cardNumber id nickname transitCardType status riderType
            tickets { id uuid productId productSku serial sunsetDateTime validToDateTime validFromDateTime properties signature signatureDateTime }
            scans { id created passDescription stationName stopId tripCost mediaId productId orderId isFirstScan }
          }
        }
        autoPurchaseProfiles {
          id
          billingProfileId
          cancelledDate
          cancelledReason
          updated
          created
          __typename
          ... on AutoLoadProfile {
            transitAccountId
            storedValueThreshold
            storedValueLoadAmount
          }
          ... on AutoRenewProfile {
            ticketId
            productSku
          }
        }
        ${
          fareCapping
            ? 'fareCaps { card_uuid user_id progress last_reset pot_id period bucket reset_unit cap_amount cap_weight product_slug pot_name cardName cardId transitAccountId isBlocked cardNumber controlNumber anonymous created }'
            : ''
        }
      }
    }
  `,
    {
      accessToken: token.accessToken,
      appId
    }
  ).then(ret => {
    if (ret.errors) {
      throw ret.errors.map(err => JSON.stringify(err));
    }
    return ret?.data?.account || ret;
  });

const fetchSingleOrderDetails = async (token, orderId) =>
  doFetch(
    `
    query ($accessToken: String!, $orderId: String!) {
      account(accessToken: $accessToken) {
        singleOrderDetails(orderId: $orderId ) {
          orderId
          created
          paymentInfo {processor amount cardType last4 expirationMonth expirationYear }
          lineItems { title qty price productId }
        }
      }
    }
  `,
    {
      accessToken: token.accessToken,
      orderId
    }
  ).then(ret => {
    if (ret.errors) {
      throw ret.errors.map(err => JSON.stringify(err));
    }
    return ret?.data?.account?.singleOrderDetails || ret;
  });

const registerAccount = async (scope, account) =>
  doFetch(
    `
    mutation($input: RegisterAccountInput!) {
      registerAccount(input: $input) {
        profile { email firstName lastName phone userId }
        notices { code message display }
      }
    }
  `,
    {
      input: {
        scope,
        username: account?.email,
        email: account?.email,
        firstName: account?.firstName,
        lastName: account?.lastName,
        phone: account?.phone,
        password: account?.password,
        communicationPreference: account?.communicationPreference
      }
    }
  ).then(ret => ret?.data?.registerAccount || ret);

const forgotPassword = async (scope, email) =>
  doFetch(
    `mutation($input: ForgotPasswordInput!) {
    forgotPassword(input: $input) {
      success
    }
  }`,
    {
      input: {
        scope,
        email
      }
    }
  ).then(ret => ret?.data?.success || ret);

const resetPassword = async (token, scope, password) =>
  doFetch(
    `mutation($rpi:ResetPasswordInput!) {
    resetPassword(input:$rpi){
      success
    }
  }`,
    {
      rpi: {
        password,
        scope,
        token
      }
    }
  );

const updateTransitCard = async (token, transitCard) =>
  doFetch(
    `mutation($accessToken: String!, $utc: UpdateTransitCardInput!) {
    account(accessToken: $accessToken) {
      updateTransitCard(input: $utc) {
        uuid
        nickname
        riderType
      }
    }
  }`,
    {
      accessToken: token?.accessToken,
      utc: {
        uuid: transitCard?.uuid,
        nickname: transitCard?.nickname,
        riderType: transitCard?.riderType
      }
    }
  );

const transferPass = async (token, passTransferDetails) =>
  doFetch(
    `mutation($accessToken: String!, $utcta: UpdateTransitCardTicketAssociationInput!) {
    account(accessToken: $accessToken) {
      updateTransitCardTicketAssociation(input: $utcta) {
        id userId uuid nickname transitAccountType status appId created expires isAutoPurchasable
      }
    }
  }`,
    {
      accessToken: token.accessToken,
      utcta: {
        transitCardUuid: passTransferDetails?.transitCardUuid,
        ticketId: passTransferDetails?.ticketId,
        ticketUuid: passTransferDetails?.ticketUuid
      }
    }
  );

const addTransitCard = async (token, cardNumber) =>
  doFetch(
    `mutation TransferTransitCard($accessToken: String!, $ttci: TransferTransitCardInput!) {
      account(token: $accessToken) {
        transferTransitCard (input: $ttci) {
            uuid
            id
            storedValue {
                balance
            }
            transitCards {
                uuid
                nickname
                cardNumber
            }
        }
  }
}`,
    {
      accessToken: token?.accessToken,
      ttci: {
        cardNumber
      }
    }
  );

const updateAccount = async (token, account) =>
  doFetch(
    `mutation($accessToken: String!, $input: UpdateAccountInput!) {
    account(accessToken: $accessToken) {
      updateAccount(input: $input)
    }
  }`,
    {
      accessToken: token?.accessToken,
      input: account
    }
  );

const updateAddress = async (scope, account) =>
  doFetch(
    `
      mutation ($input: StoreAccountAddressInput!) {
        storeAccountAddress(input: $input) {
          scope userId
          address {
            thoroughfare premise locality adminArea postalCode country
          }
        }
      }
    `,
    {
      input: {
        scope,
        userId: account?.userId,
        address: {
          thoroughfare: account?.addressLine1 ? account.addressLine1 : '',
          premise: account?.addressLine2 ? account.addressLine2 : '',
          locality: account?.city ? account.city : '',
          adminArea: account?.state ? account.state : '',
          postalCode: account?.postalCode ? account.postalCode : '',
          country: account?.country ? account.country : ''
        }
      }
    }
  ).then(ret => ret?.data?.storeAccountAddress || ret);

const fetchPaymentToken = async (token, locale) => {
  if (processor === 'braintree') {
    return doFetch(
      `
      query PaymentToken($accessToken: String!) {
        account(accessToken: $accessToken) {
          paymentToken
        }
      }
    `,
      {
        accessToken: token?.accessToken
      }
    ).then(ret => ret?.data?.account?.paymentToken || ret);
  }

  // if not braintree, assume NIC
  return doFetch(
    `
      query PaymentToken($accessToken: String!, $processor: Processor, $locale: String) {
        account(accessToken: $accessToken) {
          paymentToken(processor: $processor, locale: $locale)
        }
      }
    `,
    {
      accessToken: token?.accessToken,
      processor: processor.toUpperCase(),
      locale
    }
  ).then(ret => ret?.data?.account?.paymentToken || ret);
};

const addPaymentMethod = async (token, newPaymentMethod) =>
  doFetch(
    `
    mutation AddPaymentMethod($accessToken: String!, $input: PaymentMethodInput!) {
      account (accessToken: $accessToken) {
        addPaymentMethod(input: $input) {
          paymentMethod {
            __typename id nickname processor
            ... on CardPaymentMethod { accountNumber cardType expirationMonth expirationYear }
          }
        }
      }
    }
  `,
    {
      accessToken: token?.accessToken,
      input: {
        token: newPaymentMethod?.nonce,
        nickname: newPaymentMethod?.nickname,
        processor: processor.toUpperCase()
      }
    }
  ).then(ret => ret?.data?.account?.addPaymentMethod?.paymentMethod || ret);

const createAutoLoadProfile = async (token, autoLoadProfile) =>
  doFetch(
    `
    mutation createAutoLoadProfile($accessToken: String!, $autoLoadProfile: AutoLoadInput!) {
      account(accessToken: $accessToken) {
        createAutoLoadProfile(input: $autoLoadProfile) {
          id billingProfileId cancelledDate cancelledReason updated created transitAccountId storedValueThreshold storedValueLoadAmount
        }
      }
    }
  `,
    {
      accessToken: token?.accessToken,
      autoLoadProfile
    }
  );

const disableAutoLoadTransitAccount = async (token, transitAccountId) =>
  doFetch(
    `
    mutation disableAutoLoadTransitAccount($accessToken: String!, $transitAccountId: ID!) {
      account(accessToken: $accessToken) {
        disableAutoLoadTransitAccount(transitAccountId: $transitAccountId) {
          id billingProfileId cancelledDate cancelledReason updated created transitAccountId storedValueThreshold storedValueLoadAmount
        }
      }
    }`,
    {
      accessToken: token?.accessToken,
      transitAccountId
    }
  );

const createAutoRenewProfile = async (token, autoRenewProfile) =>
  doFetch(
    `
    mutation createAutoLoadProfile($accessToken: String!, $autoRenewProfile: AutoRenewInput!) {
      account(accessToken: $accessToken) {
        createAutoRenewProfile(input: $autoRenewProfile) {
          id billingProfileId cancelledDate cancelledReason updated created ticketId productSku
        }
      }
    }
  `,
    {
      accessToken: token?.accessToken,
      autoRenewProfile
    }
  );

const disableAutoRenewProfile = async (token, autoRenewProfileId) =>
  doFetch(
    `
  mutation disableAutoRenewProfile($accessToken: String!, $autoRenewProfileId: ID!) {
  account(accessToken: $accessToken) {
    disableAutoRenew(autoRenewProfileId: $autoRenewProfileId) {
      id billingProfileId cancelledDate cancelledReason updated created ticketId productSku
    }
  }
}`,
    {
      accessToken: token?.accessToken,
      autoRenewProfileId
    }
  );

const removePaymentMethod = async (token, id) =>
  doFetch(
    `
    mutation removePaymentMethod($accessToken: String!, $input: RemovePaymentInput!) {
      account (accessToken: $accessToken) {
        removePaymentMethod(input: $input) {
          success
        }
      }
    }
  `,
    {
      accessToken: token?.accessToken,
      input: {
        id
      }
    }
  ).then(ret => ret?.data?.account?.removePaymentMethod?.success || ret);

const existingPaymentMethodPurchase = async (
  token,
  {paymentMethod, cartPasses}
) =>
  doFetch(
    `mutation ExistingPaymentMethodPurchase($accessToken: String!, $input: PurchaseInput!) {
    account(accessToken: $accessToken) {
      purchase(input: $input, blocking: false) {
        success
        tickets { id productId productSku serial sunsetDate validFrom validTo properties }
      }
    }
  }`,
    {
      accessToken: token?.accessToken,
      input: {
        currency: 'USD',
        products: cartPasses.map(item => ({
          id: item?.id,
          price: item?.price,
          quantity: item?.quantity,
          transitCardUuid: item?.uuid,
          meta: {
            origin: item?.origin_id,
            destination: item?.destination_id
          }
        })),
        payment: {
          existingPayment: {
            id: paymentMethod?.id,
            amount: cartPasses
              .map(
                item =>
                  (Number(item?.price) || 0) * (Number(item?.quantity) || 0)
              )
              .reduce((acc, subTotal) => acc + subTotal, 0),
            processor: processor.toUpperCase()
          }
        },
        device: {latitude: 0, longitude: 0}
      }
    }
  ).then(ret => {
    if (ret?.errors) {
      throw ret.errors.map(err => JSON.stringify(err));
    }
    return ret;
  });

const existingPaymentMethodAddBalance = async (
  token,
  {paymentMethod, transitAccount, amount}
) =>
  doFetch(
    `mutation ExistingPaymentMethodAddBalance($accessToken: String!, $input: PurchaseInput!) {
    account (token: $accessToken) {
      purchase (input: $input, blocking: true) {
        success
        transitAccount {
          id nickname transitAccountType riderType userId
          passes { id }
          storedValue {
            balance
            buckets { id nickname amount bucketType }
          }
        }
      }
    }
  }`,
    {
      accessToken: token.accessToken,
      input: {
        currency: 'USD',
        value: [{transitAccountId: transitAccount?.id, amount}],
        payment: {
          existingPayment: {
            id: paymentMethod.id,
            processor: processor.toUpperCase(),
            amount
          }
        },
        device: {latitude: 0, longitude: 0}
      }
    }
  ).then(ret => {
    if (ret?.errors) {
      throw ret.errors.map(err => JSON.stringify(err));
    }
    return ret;
  });

const oneTimePaymentPurchase = async (
  token,
  {oneTimePaymentToken, cartPasses}
) =>
  doFetch(
    `mutation OneTimePaymentPurchase($accessToken: String!, $input: PurchaseInput!) {
    account (token: $accessToken) {
      purchase (input: $input, blocking: true) {
        success
      }
    }
  }`,
    {
      accessToken: token.accessToken,
      input: {
        currency: 'USD',
        products: cartPasses.map(item => ({
          id: item.id,
          price: item.price,
          quantity: item.quantity,
          transitCardUuid: item.transit_card_uuid,
          meta: {
            origin: item.origin_id,
            destination: item.destination_id
          }
        })),
        payment: {
          newCards: [
            {
              token: btoa(JSON.stringify({oneTimePaymentToken})),
              processor: processor.toUpperCase(),
              amount: cartPasses
                .map(
                  item =>
                    (Number(item?.price) || 0) * Number(item?.quantity || 0)
                )
                .reduce((acc, subTotal) => acc + subTotal, 0),
              store: false
            }
          ]
        },
        device: {latitude: 0, longitude: 0}
      }
    }
  ).then(ret => {
    if (ret.errors) {
      throw ret.errors.map(err => JSON.stringify(err));
    }
    return ret;
  });

const oneTimePaymentAddBalance = async (
  token,
  {oneTimePaymentToken, transitAccountId, amount}
) =>
  doFetch(
    `mutation OneTimePaymentAddBalance($accessToken: String!, $input: PurchaseInput!) {
    account (token: $accessToken) {
      purchase (input: $input, blocking: true) {
        success
      }
    }
  }`,
    {
      accessToken: token.accessToken,
      input: {
        currency: 'USD',
        value: [{transitAccountId, amount}],
        payment: {
          newCards: [
            {
              token: btoa(JSON.stringify({oneTimePaymentToken})),
              processor: processor.toUpperCase(),
              amount,
              store: false
            }
          ]
        },
        device: {latitude: 0, longitude: 0}
      }
    }
  ).then(ret => {
    if (ret.errors) {
      throw ret.errors.map(err => JSON.stringify(err));
    }
    return ret;
  });

const transitionAccountLookup = (token, scope, data) => {
  const {email, username, cardNumber} = data;
  const params1 = [];
  const params2 = [];

  if (email) {
    params1.push('$email: String!');
    params2.push('email: $email');
  }
  if (username) {
    params1.push('$username: String!');
    params2.push('username: $username');
  }
  if (cardNumber) {
    params1.push('$cardNumber: String!');
    params2.push('cardNumber: $cardNumber');
  }
  const paramsStr1 = params1.join(', ');
  const paramsStr2 = params2.join(', ');

  return doFetch(
    `query(${paramsStr1}) { lookupAccount (${paramsStr2}) {
        status
        accessRequestId
        transitAccountId
    }
  }`,
    data,
    {
      'x-gs-access-token': token.accessToken,
      'x-gs-scope': scope
    }
  );
};

const transitionGetSecurityQuestion = (token, scope, data) =>
  doFetch(
    `query($accessRequestId: String!) {
      getSecurityQuestion(accessRequestId: $accessRequestId){
          question
      }
    }`,
    data,
    {
      'x-gs-access-token': token.accessToken,
      'x-gs-scope': scope
    }
  );

const transitionAnswerSecurityQuestion = (token, scope, data) =>
  doFetch(
    `query TransitionAnswerSecurityQuestion($accessRequestId: String!, $answer: String!) {
      sendSecurityAnswer(accessRequestId: $accessRequestId, answer: $answer){
          success
          errors
      }
    }`,
    data,
    {
      'x-gs-access-token': token.accessToken,
      'x-gs-scope': scope
    }
  );

const transitionGetProducts = (token, scope, data) =>
  doFetch(
    `query($accessRequestId:String!){
      getProducts(accessRequestId: $accessRequestId){
          fare_media_number
          product
          bonus_amount
          purchase_amount
          total_amount
          start_date
          expiration_date
      }
  }`,
    data,
    {
      'x-gs-access-token': token.accessToken,
      'x-gs-scope': scope
    }
  );

const transitionTransferProducts = (token, scope, data) =>
  doFetch(
    `query($accessRequestId: String!){
      transferProducts(accessRequestId: $accessRequestId){
          transaction_id
          media_count
          operational_status_code
      }
  }`,
    data,
    {
      'x-gs-access-token': token.accessToken,
      'x-gs-scope': scope
    }
  );

const transitionConfirm = (token, scope, data) =>
  doFetch(
    `query($accessRequestId: String!, $transactionId: String!){
      confirm(accessRequestId: $accessRequestId, transactionId:$transactionId){
          success
          errors
      }
  }`,
    data,
    {
      'x-gs-access-token': token.accessToken,
      'x-gs-scope': scope
    }
  );

export {
  loginUser,
  refreshAccessToken,
  fetchAccount,
  fetchSingleOrderDetails,
  registerAccount,
  updateAccount,
  updateAddress,
  updateTransitCard,
  addTransitCard,
  transferPass,
  forgotPassword,
  resetPassword,
  fetchContent,
  fetchPaymentToken,
  addPaymentMethod,
  removePaymentMethod,
  createAutoLoadProfile,
  disableAutoLoadTransitAccount,
  createAutoRenewProfile,
  disableAutoRenewProfile,
  existingPaymentMethodPurchase,
  existingPaymentMethodAddBalance,
  oneTimePaymentPurchase,
  oneTimePaymentAddBalance,
  transitionAccountLookup,
  transitionGetSecurityQuestion,
  transitionAnswerSecurityQuestion,
  transitionGetProducts,
  transitionTransferProducts,
  transitionConfirm
};
